<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    :title="mode === 'R' || mode === 'E' ? 'クラウド向けQoS設定' : '確認'"
  >
    <template v-slot:body="{ ok, cancel }">
      <!-- BODY -->
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container>
            <!-- 編集モード -->
            <v-row v-if="mode === 'E'">
              <v-col class="d-flex flex-row justify-end">
                <v-btn
                  v-permission.config
                  color="primary"
                  @click="showModifyModal"
                  >変更</v-btn
                >
              </v-col>
            </v-row>
            <!-- 確認モード -->
            <v-row class="mb-2" v-else-if="mode === 'C'">
              <label v-if="isNew" class="font-weight-bold"
                >こちらの内容でクラウド向けQoSを設定します。よろしいですか？</label
              >
              <label v-else class="font-weight-bold"
                >こちらの内容でクラウド向けQoS設定を変更します。よろしいですか？</label
              >
            </v-row>

            <!-- 未登録の場合 -->
            <template v-if="cloudQosDetail.qosType === 'NONE'">
              <v-row>
                <v-col cols="12">
                  <app-label-group label="QoS方式" value="設定無し" />
                </v-col>
              </v-row>
            </template>

            <!-- QoS設定がある場合（QoS方式：絶対優先方式） -->
            <template v-if="cloudQosDetail.qosType === 'ABSOLUTE'">
              <v-row>
                <v-col cols="12">
                  <app-label-group label="QoS方式" value="絶対優先方式" />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <app-label-group
                    label="優先度参照先"
                    :value="
                      $filter.enumTo(
                        cloudQosDetail.priorityTarget,
                        'cloudQos_PriorityTarget'
                      )
                    "
                  />
                </v-col>
              </v-row>
              <h3 class="mt-3">優先度割当</h3>
              <v-table density="compact" class="table-bordered mt-3">
                <thead class="text-center">
                  <tr>
                    <th colspan="2">IP Precedence</th>
                    <th v-for="index in 8" :key="index">{{ index - 1 }}</th>
                  </tr>
                  <tr>
                    <th colspan="2">DSCP</th>
                    <th
                      v-for="(
                        colName, colNameIndex
                      ) in absolutePriorityColNames"
                      :key="colNameIndex"
                      class="absolute-width"
                    >
                      {{ colName }}
                    </th>
                  </tr>
                </thead>
                <tbody class="text-center">
                  <th :rowspan="5">優先度</th>
                  <tr
                    v-for="(rowName, rowNameIndex) in absolutePriorityRowNames"
                    :key="rowNameIndex"
                  >
                    <th class="text-left table-light-color">
                      {{ rowName.value }}
                    </th>
                    <td
                      v-for="(
                        propName, propNameIndex
                      ) in absolutePriorityPropNames"
                      :key="propNameIndex"
                    >
                      {{
                        cloudQosDetail.cloudQosAbsolute![propName] ===
                        rowName.key
                          ? "●"
                          : "○"
                      }}
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </template>

            <!-- QoS設定がある場合（QoS方式：帯域制御方式） -->
            <template v-if="cloudQosDetail.qosType === 'BANDWIDTH'">
              <v-row>
                <v-col cols="12">
                  <app-label-group label="QoS方式" value="帯域制御方式" />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <app-label-group
                    label="優先度参照先"
                    :value="
                      $filter.enumTo(
                        cloudQosDetail.priorityTarget,
                        'cloudQos_PriorityTarget'
                      )
                    "
                  />
                </v-col>
              </v-row>
              <h3 class="mt-3">優先度割当</h3>
              <v-table density="compact" class="table-bordered mt-3">
                <thead>
                  <tr>
                    <th colspan="2" rowspan="2"></th>
                    <th colspan="5" class="text-center">DSCP</th>
                  </tr>
                  <tr>
                    <th
                      class="text-left"
                      v-for="(colName, colNameIndex) in bandwidthColNames"
                      :key="colNameIndex"
                    >
                      {{ colName }}
                    </th>
                  </tr>
                </thead>
                <tbody class="text-left">
                  <tr
                    v-for="(rowName, rowNameIndex) in bandwidthRowNames"
                    :key="rowNameIndex"
                  >
                    <template v-if="rowNameIndex % 2 === 0">
                      <th rowspan="2">{{ rowName.value }}</th>
                      <th>割当帯域</th>
                      <td
                        v-for="(propName, propNameIndex) in bandwidthPropNames"
                        :key="propNameIndex"
                        class="text-right"
                      >
                        {{ getBandwidthValue(rowName.key, propName) }}
                      </td>
                    </template>
                    <template v-else>
                      <th>最優先</th>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td class="text-right">
                        {{ isDscp46TopPriority(rowName.key) }}
                      </td>
                      <td></td>
                    </template>
                  </tr>
                </tbody>
              </v-table>
            </template>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <!-- FOOTER -->
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <!-- 参照モード -->
          <template v-if="mode === 'R' || mode === 'E'" v-slot:append>
            <v-btn @click="cancel" variant="outlined" color="primary"
              >戻る</v-btn
            >
          </template>
          <!-- 参照モード -->
          <template v-else-if="mode === 'C'" v-slot:append>
            <v-btn @click="ok" variant="flat" color="primary">はい</v-btn>
            <v-btn @click="cancel" variant="outlined" color="primary"
              >いいえ</v-btn
            >
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import type { CloudQosDetail } from "@/apis/CloudQosApi";
import { VueFinalModal } from "vue-final-modal";
import CloudQosEdit from "@/modals/cloudQos/CloudQosEdit.vue";

export default defineComponent({
  name: "CloudQosViewRC",
  props: {
    // 変更画面で使用
    vpnVnCode: {
      type: String as PropType<string>,
      required: false,
      default: "",
    },
    // 変更画面で使用
    vnName: {
      type: String as PropType<string>,
      required: false,
      default: "",
    },
    // E(編集) or C(確認) or R(参照)
    mode: {
      type: String as PropType<string>,
      required: false,
      default: "E",
    },
    isNew: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    cloudQosDetail: {
      type: Object as PropType<CloudQosDetail>,
      required: true,
    },
  },
  data() {
    return {
      absolutePriorityRowNames: [
        { key: "HIGH", value: "High" },
        { key: "MEDIUM", value: "Medium" },
        { key: "NORMAL", value: "Normal" },
        { key: "LOW", value: "Low" },
      ],
      absolutePriorityColNames: ["0", "10", "18", "24", "34", "46", "-", "-"],
      absolutePriorityPropNames: [
        "priority0",
        "priority1",
        "priority2",
        "priority3",
        "priority4",
        "priority5",
        "priority6",
        "priority7",
      ] as Array<keyof CloudQosDetail["cloudQosAbsolute"]>,
      bandwidthColNames: ["18", "24", "34", "46", "ベストエフォート"],
      bandwidthRowNames: [
        { key: "PATTERN_A", value: "パターンA" },
        { key: "PATTERN_A", value: "" },
        { key: "PATTERN_B", value: "パターンB" },
        { key: "PATTERN_B", value: "" },
      ],
      bandwidthPropNames: [
        "dscp18Value",
        "dscp24Value",
        "dscp34Value",
        "dscp46Value",
        "bestEffort",
      ],
    };
  },
  methods: {
    async showModifyModal() {
      await this.$modal.show(CloudQosEdit, {
        vpnVnCode: this.vpnVnCode,
        vnName: this.vnName,
        isNew: this.isNew,
        cloudQosDetail: this.cloudQosDetail,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /**
     * 帯域制御方式の値を取得
     * @param patternName パターン名
     * @param propName プロパティ名
     * */
    getBandwidthValue: function (patternName: string, propName: string) {
      const item = this.cloudQosDetail.cloudQosBandwidthList?.find(
        (e) => e.patternName === patternName
      );
      if (item) {
        if (propName === "bestEffort") {
          return (item.dscp18Value || 0) +
            (item.dscp24Value || 0) +
            (item.dscp34Value || 0) +
            (item.dscp46Value || 0) >
            0
            ? `${
                100 -
                ((item.dscp18Value || 0) +
                  (item.dscp24Value || 0) +
                  (item.dscp34Value || 0) +
                  (item.dscp46Value || 0))
              }%`
            : "-";
        } else {
          switch (propName) {
            case "dscp18Value":
              return item.dscp18Value ? `${item.dscp18Value}%` : "-";
            case "dscp24Value":
              return item.dscp24Value ? `${item.dscp24Value}%` : "-";
            case "dscp34Value":
              return item.dscp34Value ? `${item.dscp34Value}%` : "-";
            case "dscp46Value":
              return item.dscp46Value ? `${item.dscp46Value}%` : "-";
          }
        }
      } else {
        return "-";
      }
    },
    /**
     * 帯域制御方式の最優先設定フラグを取得
     * @param patternName パターン名
     * */
    isDscp46TopPriority(patternName: string) {
      const item = this.cloudQosDetail.cloudQosBandwidthList?.find(
        (e) => e.patternName === patternName
      );
      if (item) {
        return item.isDscp46TopPriority ? "ON" : "OFF";
      } else {
        return "-";
      }
    },
  },
});
</script>

<style lang="scss" scoped>
th {
  background-color: #e9ecef;
  font-weight: bold !important;
  padding: 4px !important;
}

.text-center th {
  text-align: center !important;
}

.text-left th {
  text-align: left !important;
}

.absolute-width {
  width: 50px;
}
</style>
